import { Card, CardBody } from '@statsbomb/kitbag-components';
import {
  Controls,
  PlayerAndPlaylistWrapper,
  Playlist,
  VideoEvent,
  VideoFull,
  VideoPlay,
  configureVideoControls,
} from '@statsbomb/video-client-js';
import { useAtom, useAtomValue } from 'jotai';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { selectedMetadataAtom } from '../../atoms/selectedMetadata';
import {
  BALL_IN_PLAY,
  EVENTS,
  MATCH,
  MATCH_EVENTS,
  PLAYS,
  selectedIdsAtom,
  selectedVideoReferenceAtom,
  videoTypeAtom,
} from '../../atoms/selectedVideoData';
import CardHeader from '../../components/CardHeader';
import EventSearchBox from '../../components/EventSearchBox';
import FullVideoSearchBox from '../../components/FullVideoSearchBox';
import { MatchPeriodVideoSearch } from '../../components/MatchPeriodVideoSearch/MatchPeriodVideoSearch';
import PlaySearchBox from '../../components/PlaySearchBox';
import VideoTypeSelection from '../../components/VideoTypeSelection';
import { useClearSelections } from '../../hooks/clearSelectionsOnLoad';
import MatchEvents from '../Player/ClipAdding/MatchEvents';

export function PlayerBasic() {
  const intl = useIntl();
  const [events, setEvents] = useState();
  const [plays, setPlays] = useState();
  const [selectedIds, setSelectedIds] = useAtom(selectedIdsAtom);

  const [videoReference, setVideoReference] = useAtom(
    selectedVideoReferenceAtom,
  );

  const [videoType, setVideoType] = useAtom(videoTypeAtom);

  useEffect(() => setVideoType(EVENTS), []);

  const playlistTitle =
    videoType === BALL_IN_PLAY &&
    videoReference?.matchId &&
    intl.formatMessage(
      { defaultMessage: 'Ball In Play #{matchId}', id: 'b0d42' },
      { matchId: videoReference?.matchId },
    );
  const { resetSelections } = useClearSelections();

  const clear = () => {
    setEvents();
    setSelectedIds([]);
    setPlays();
    setVideoReference();
    resetSelections();
  };

  useEffect(clear, [videoType]);

  const selectionMade =
    events || plays || videoReference || selectedIds?.length > 0;
  const selectedMetadata = useAtomValue(selectedMetadataAtom);

  const onVideoSearch = () => {
    const { sport, matchId, period } = selectedMetadata;
    setVideoReference({ matchId, period, sport });
  };

  const controls = configureVideoControls({
    controlsToIncludeInAdditionToDefault: [Controls.SaveCurrentlyPlaying],
  });

  const defaultConfig = {
    autoPlay: true,
    muted: true,
  };

  return selectionMade ? (
    <>
      <PlayerAndPlaylistWrapper>
        <Playlist
          enableEditMode
          enableReordering
          enableSelectAll
          onBack={clear}
          title={playlistTitle}
        />
        {/* Props spreading defaultConfig ensures single definition of configuration */}
        {/* which is consistent across all the players included here */}
        {/* eslint-disable react/jsx-props-no-spreading */}
        {videoType === BALL_IN_PLAY && (
          <VideoFull
            {...defaultConfig}
            ballInPlay
            videoReference={{
              matchId: videoReference.matchId,
              period: videoReference.period,
              sport: videoReference.sport,
            }}
          />
        )}
        {videoType === EVENTS && (
          <VideoEvent {...defaultConfig} controls={controls} events={events} />
        )}
        {videoType === MATCH_EVENTS && (
          <VideoEvent
            {...defaultConfig}
            controls={controls}
            events={selectedIds}
          />
        )}
        {videoType === MATCH && (
          <VideoFull {...defaultConfig} videoReference={videoReference} />
        )}
        {videoType === PLAYS && (
          <VideoPlay {...defaultConfig} controls={controls} plays={plays} />
        )}
        {/* eslint-enable react/jsx-props-no-spreading */}
      </PlayerAndPlaylistWrapper>
      {videoType === MATCH_EVENTS && videoReference && <MatchEvents />}
    </>
  ) : (
    <div>
      <Card>
        <CardHeader>
          <FormattedMessage
            defaultMessage="Select Video Source"
            description="Select Video Source"
            id="f9580"
          />
        </CardHeader>
        <CardBody>
          <VideoTypeSelection
            allowBallInPlay
            allowIdProvision
            allowMatchEvents
          />
          {videoType === MATCH && (
            <FullVideoSearchBox onSearch={setVideoReference} />
          )}
          {videoType === EVENTS && <EventSearchBox setEvents={setEvents} />}
          {videoType === PLAYS && <PlaySearchBox setPlays={setPlays} />}
          {videoType === BALL_IN_PLAY && (
            <MatchPeriodVideoSearch onVideoSearch={onVideoSearch} />
          )}
          {videoType === MATCH_EVENTS && (
            <MatchPeriodVideoSearch onVideoSearch={onVideoSearch} />
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export default { PlayerBasic };
